import { QueryObserverResult, useQuery } from "@tanstack/vue-query";
import mapboxApi from "../api/mapboxApi";
import { useMapboxSearchStore } from "@/stores/mapboxSearch";
import { storeToRefs } from "pinia";
import recursivelyToCamel from "@/helpers/recursivelyToCamel";
import type { MapboxSearchRetrieveResponse } from "@/interfaces/mapboxSearchRetrieveResponse";
import { watch } from "vue";
import { MapboxSessionData } from "@/interfaces/mapboxSessionData";

const access_token = import.meta.env.VITE_MAPBOX_ACCESS_TOKEN;

interface Params {
  session_token: string;
}

const getSearchRetrieve = async (
  mapboxId: string,
  params: Params,
  sessionData: Pick<MapboxSessionData, "resetApiValues">
): Promise<MapboxSearchRetrieveResponse | void> => {
  try {
    const { resetApiValues } = sessionData;

    const { data } = await mapboxApi.get(
      `/search/searchbox/v1/retrieve/${mapboxId}`,
      {
        params: {
          ...params,
          access_token,
        },
      }
    );

    resetApiValues(true);

    return recursivelyToCamel(data) as MapboxSearchRetrieveResponse;
  } catch (err) {
    // Error occurred
  }
};

const useSearchBoxRetrieve = () => {
  const { setRetrievePlace, resetApiValues } = useMapboxSearchStore();
  const {
    selectedPlaceId,
    retrievedPlace,
    sessionToken,
    retrievePlaceCoordinates,
  } = storeToRefs(useMapboxSearchStore());

  const { data, error, refetch, isFetching } = useQuery(
    ["searchedBoxRetrieve"],
    () =>
      getSearchRetrieve(
        selectedPlaceId.value,
        {
          session_token: sessionToken.value,
        },
        { resetApiValues }
      ),
    {
      retry: false,
      enabled: false,
    }
  );

  watch(data, (value) => {
    if (value) {
      setRetrievePlace(value);
    }
  });

  return {
    retrievedPlace,
    isFetching,
    retrievePlaceCoordinates,
    error,

    retrievePlace: async (): Promise<ReturnType<typeof refetch> | void> => {
      if (selectedPlaceId.value) return await refetch();
    },
  };
};

export default useSearchBoxRetrieve;
